import {memo, useMemo} from 'react';

import styled from 'styled-components';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';

import {SkeletonLoader} from '~/shared/components/Loaders';
import Button from '~/shared/components/Button';
import ArrowLeft from '~/assets/images/svgIconsAsText/back-arrow.svg?react';
import {flexColumnCenterVertically, flexColumnCenter, flexCenter} from '~/shared/theme/FlexLayout';
import {getLocalizationService} from '~/shared/services/localisationService';

const BUTTON_SIZE_PX = 15;
const BUTTON_SIDE_MARGIN_PX = 12;

const DateText = styled.div`
  margin: 0 auto;
  ${flexColumnCenterVertically};
  color: ${({theme}) => theme.userReport.dateSelection.text};
`;

const ModifiedSkeletonLoader = styled(SkeletonLoader)`
  width: 152px;
  height: 16px;
  margin: auto;
`;

const StyledButton = styled(Button)`
  width: ${BUTTON_SIZE_PX}px;
  margin: 0 ${BUTTON_SIDE_MARGIN_PX}px;
  ${flexColumnCenter};
  z-index: 1;
  display: flex;
  cursor: pointer;
  ${({forward: isForwardArrow}) =>
    isForwardArrow &&
    `
    transform: rotate(180deg);
  `}
  &&& {
    ${({isDisabled}) =>
    isDisabled &&
      `
      background-color: transparent; 
      cursor: default;
    `}
  }
`;

const ArrowIcon = styled(ArrowLeft)`
  display: flex;

  path {
    fill: ${({theme}) => theme.userReport.dateSelection.text};
  }
`;

const ButtonWrapper = styled.div`
  ${flexCenter}
  min-width: ${BUTTON_SIZE_PX + (BUTTON_SIDE_MARGIN_PX * 2)}px;
`;

export type DateSelectionProps = {
  startDateStr: string;
  endDateStr: string;
  stepForward: () => void;
  stepBackward: () => void;
  allowedToStepForward: boolean;
  showMonthOnly?: boolean;
  allowedToStepBackward?: boolean;
};

const DateSelection = ({
  startDateStr,
  endDateStr,
  stepForward,
  stepBackward,
  allowedToStepForward,
  showMonthOnly = false,
  allowedToStepBackward = true,
}: DateSelectionProps) => {
  const {t, currentLanguageKey} = getLocalizationService();

  const memoizedDate = useMemo(() => {
    if (!startDateStr || !endDateStr) {
      return {
        start: undefined,
        end: undefined,
        isStartAndEndHasSameYear: undefined,
      };
    }

    const startDate = moment(startDateStr, 'DD/MM/YYYY').locale(currentLanguageKey);
    const endDate = moment(endDateStr, 'DD/MM/YYYY').locale(currentLanguageKey);
    const start = {
      day: startDate.format('D'),
      monthName: startDate.format('MMMM'),
      year: startDate.format('YYYY'),
    };
    const end = {
      day: endDate.format('D'),
      monthName: endDate.format('MMMM'),
      year: endDate.format('YYYY'),
    };
    return {
      start,
      end,
      isStartAndEndHasSameYear: start.year === end.year,
    };
  }, [currentLanguageKey, endDateStr, startDateStr]);

  const {start, end, isStartAndEndHasSameYear} = memoizedDate || {};

  const dateString = (showMonthOnly && start?.monthName === end?.monthName) ?
    `${start?.monthName} ${start?.year}` :
    `${start?.day} ${start?.monthName} ${isStartAndEndHasSameYear ? '' : start?.year} - ${end?.day} ${
      end?.monthName
    }, ${end?.year}`;

  return (
    <ModifiedSkeletonLoader shouldShowLoader={!start || !end}>
      <>
        <ButtonWrapper>
          {allowedToStepBackward && (
          <StyledButton onClick={stepBackward} aria-label={t('previous_month')} isDisabled={!allowedToStepBackward}>
            <ArrowIcon />
          </StyledButton>
        )}
        </ButtonWrapper>

        <DateText data-test-id="dateSelectionText">
          {dateString}
        </DateText>
        <ButtonWrapper>
          {allowedToStepForward && (
          <StyledButton
            aria-label={t('next_month')}
            onClick={stepForward}
            forward
          >
            <ArrowIcon />
          </StyledButton>
        )}
        </ButtonWrapper>
      </>
    </ModifiedSkeletonLoader>
  );
};

export default memo(DateSelection);
